// Kubota-style PM interval grid.
// Rows = maintenance items, columns = hour markers (50, 100, 150, ...),
// circles colored by state: white=upcoming, yellow=pending, green=done, red=overdue,
// grey=before the service baseline (assumed handled before the machine was added).
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api, fmtDate } from '../lib/api.js';

export default function PmGrid({ equipmentId, canManage, onChange, reloadSignal }) {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(null); // task id being acted on
  const [err, setErr] = useState('');
  const [editing, setEditing] = useState(null); // task obj being edited / null
  const [adding, setAdding] = useState(false);

  async function load() {
    try { setData(await api.get(`/api/equipment/${equipmentId}/pm-grid`)); }
    catch (e) { setErr(e.message); }
  }
  // Reload whenever the equipment changes OR the parent bumps reloadSignal
  // (e.g. after approving a pending PM task or extracting a manual).
  useEffect(() => { load(); }, [equipmentId, reloadSignal]);

  if (err) return <div className="text-rust-700 text-sm">{err}</div>;
  if (!data) return <div className="text-ink-700/60 italic">Loading…</div>;

  async function markDone(taskId, hour) {
    setBusy(taskId);
    try {
      await api.post(`/api/pm-tasks/${taskId}/done`, { hour_meter: hour });
      await load();
      onChange?.();
    } catch (e) { setErr(e.message); }
    finally { setBusy(null); }
  }

  async function undoDone(taskId, hour) {
    if (!confirm(`Undo this completion?${hour != null ? ` (interval at ${hour}hr)` : ''}`)) return;
    setBusy(taskId);
    try {
      const q = hour != null ? `?hour=${hour}` : '';
      await api.delete(`/api/pm-tasks/${taskId}/done${q}`);
      await load();
      onChange?.();
    } catch (e) { setErr(e.message); }
    finally { setBusy(null); }
  }

  async function saveTask(form, taskId = null) {
    const body = {
      description: form.description.trim(),
      interval_hours: form.interval_hours ? Number(form.interval_hours) : null,
      interval_days: form.interval_days ? Number(form.interval_days) : null,
    };
    if (!body.description) throw new Error('description required');
    if (!body.interval_hours && !body.interval_days) throw new Error('one of interval_hours or interval_days required');
    let id = taskId;
    if (taskId) await api.patch(`/api/pm-tasks/${taskId}`, body);
    else id = (await api.post('/api/pm-tasks', { ...body, equipment_id: equipmentId })).id;
    if (form.last_done_hours || form.last_done_at) {
      await api.post(`/api/pm-tasks/${id}/backfill`, {
        last_done_hours: form.last_done_hours ? Number(form.last_done_hours) : null,
        last_done_at: form.last_done_at || null,
      });
    }
    await load();
    onChange?.();
  }

  async function deleteTask(taskId) {
    if (!confirm('Delete this PM task? It comes off the schedule and the calendar.')) return false;
    await api.patch(`/api/pm-tasks/${taskId}`, { status: 'deleted' });
    await load();
    onChange?.();
    return true;
  }

  // Tasks with no baseline and no completion show their whole history as overdue.
  const hasUnanchored = data.tasks.some(t => t.baseline_hours == null && t.last_done_hours == null);
  async function resetBaseline() {
    if (!confirm(`Treat every service before ${data.hour_meter} hr as already handled?\n\nOnly affects tasks that have never been marked done. Their next service will land on the next interval mark.`)) return;
    try {
      await api.post(`/api/equipment/${equipmentId}/pm-baseline`);
      await load();
      onChange?.();
    } catch (e) { setErr(e.message); }
  }

  const noActive = data.tasks.length === 0 && data.calendar_tasks.length === 0;
  if (noActive) {
    return (
      <div className="space-y-3">
        <p className="text-sm text-ink-700/60 italic">
          No active PM tasks yet. Upload a manual to auto-extract, approve pending tasks above, or add a custom one.
        </p>
        {canManage && <button onClick={() => setAdding(true)} className="btn-ghost text-sm">+ Add custom PM task</button>}
        {(adding || editing) && (
          <Sheet onClose={() => { setAdding(false); setEditing(null); }}>
            <PmTaskForm task={editing} onSave={async (f) => { await saveTask(f, editing?.id); setAdding(false); setEditing(null); }} onCancel={() => { setAdding(false); setEditing(null); }} />
          </Sheet>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between text-xs flex-wrap gap-2">
        <Legend />
        <div className="text-ink-700/60">
          Current hour meter: <strong>{data.hour_meter}hr</strong>
          {data.pm_schedule && (
            <Link
              to={`/manuals/${data.pm_schedule.manual_id}?page=${data.pm_schedule.page}`}
              className="ml-3 pill bg-gold-500/15 text-gold-600 hover:bg-gold-500/25"
              title="Open the master service-interval table from the manual"
            >📋 PM Schedule (manual page {data.pm_schedule.printed_page ?? data.pm_schedule.page})</Link>
          )}
        </div>
      </div>

      {data.tasks.length > 0 && (
        <div className="overflow-x-auto -mx-4 px-4">
          <table className="text-xs border-separate border-spacing-y-0.5">
            <thead>
              <tr className="text-[10px] uppercase tracking-wider text-ink-700/60">
                <th className="text-left pr-3 pb-1 sticky left-0 bg-white z-10">Item</th>
                <th className="text-left pr-2 pb-1">Every</th>
                {data.markers.map(m => (
                  <th key={m} className={`text-center px-1 pb-1 min-w-[28px] ${m === Math.floor(data.hour_meter / data.step) * data.step ? 'text-olive-900 font-bold' : ''}`}>
                    {m}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {data.tasks.map(t => (
                <tr key={t.id} className="hover:bg-cream-100/40">
                  <td className="text-left pr-3 py-1 sticky left-0 bg-white whitespace-nowrap text-ink-900 z-10">
                    <span>{t.description}</span>
                    {t.source_manual_id && t.source_page && (
                      <Link
                        to={`/manuals/${t.source_manual_id}?page=${t.source_page}`}
                        className="ml-2 text-xs text-olive-700 hover:underline"
                        title={`Manual page ${t.printed_page ?? t.source_page}`}
                      >p{t.printed_page ?? t.source_page}</Link>
                    )}
                    {canManage && (
                      <button
                        onClick={() => setEditing(t)}
                        className="ml-2 px-1.5 py-1 rounded text-xs text-olive-700 underline underline-offset-2 hover:bg-olive-50"
                        title="Edit or delete this task"
                        aria-label={`Edit ${t.description}`}
                      >edit</button>
                    )}
                  </td>
                  <td className="text-left pr-2 py-1 text-ink-700/60 whitespace-nowrap">
                    {t.interval_hours}hr{t.interval_days ? ` / ${t.interval_days}d` : ''}
                  </td>
                  {t.cells.map(c => (
                    <td key={c.hour} className="text-center px-1 py-1">
                      <Circle
                        cell={c}
                        taskId={t.id}
                        onClick={
                          c.state === 'yellow' || c.state === 'red'
                            ? () => markDone(t.id, c.hour)
                            : c.state === 'green'
                              ? () => undoDone(t.id, c.hour)
                              : null
                        }
                        busy={busy === t.id}
                      />
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
          <p className="text-xs text-ink-700/50 mt-2">
            Tap yellow/red to mark done, green to undo. “edit” changes an interval, records a past service, or deletes the task. Page numbers open the manual.
          </p>
          {canManage && (
            <div className="mt-3 flex flex-wrap gap-2">
              <button onClick={() => setAdding(true)} className="btn-ghost text-sm">+ Add custom PM task</button>
              {hasUnanchored && (
                <button onClick={resetBaseline} className="btn-ghost text-sm" title="For tasks never marked done: treat services before the current hour meter as handled">
                  Start schedule from current hours
                </button>
              )}
            </div>
          )}
        </div>
      )}

      {(adding || editing) && (
        <Sheet onClose={() => { setAdding(false); setEditing(null); }}>
          <PmTaskForm
            task={editing}
            onSave={async (f) => { await saveTask(f, editing?.id); setAdding(false); setEditing(null); }}
            onDelete={editing ? async () => { if (await deleteTask(editing.id)) setEditing(null); } : null}
            onCancel={() => { setAdding(false); setEditing(null); }}
          />
        </Sheet>
      )}

      {data.calendar_tasks.length > 0 && (
        <div className="space-y-1.5">
          <h3 className="text-xs uppercase tracking-wider text-ink-700/60">Calendar items (no hour interval)</h3>
          <ul className="space-y-1">
            {data.calendar_tasks.map(t => (
              <li key={t.id} className="flex items-center gap-3 text-sm border-l-2 border-olive-100 pl-3 py-1">
                <Circle cell={{ state: t.state }} taskId={t.id} onClick={null} busy={false} />
                <span className="font-medium">{t.description}</span>
                <span className="text-ink-700/60 text-xs">every {t.interval_days}d</span>
                <span className="text-ink-700/50 text-xs ml-auto">
                  {t.last_done_at && <>last {fmtDate(t.last_done_at)}</>}
                  {t.last_done_at && t.next_due_at && ' · '}
                  {t.next_due_at && <>next {fmtDate(t.next_due_at)}</>}
                </span>
                <button
                  onClick={() => markDone(t.id, null)}
                  disabled={busy === t.id}
                  className="text-xs text-olive-700 hover:underline disabled:opacity-50"
                >
                  Mark done
                </button>
                {canManage && (
                  <button
                    onClick={() => setEditing(t)}
                    className="px-1.5 py-1 rounded text-xs text-olive-700 underline underline-offset-2 hover:bg-olive-50"
                    aria-label={`Edit ${t.description}`}
                  >edit</button>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

function Circle({ cell, onClick, busy }) {
  if (cell.state === 'empty') return null;
  const base = 'inline-block shrink-0 rounded-full border-2 transition';
  let cls = '';
  let title = '';
  switch (cell.state) {
    case 'white':  cls = 'border-ink-700/30 bg-white';            title = 'Upcoming'; break;
    case 'yellow': cls = 'border-gold-600 bg-gold-500/30';        title = 'Due'; break;
    case 'green':  cls = 'border-olive-700 bg-olive-700';         title = `Done${cell.completed_at ? ` (${cell.completed_at.slice(0, 10)})` : ''} — click to undo`; break;
    case 'red':    cls = 'border-rust-700 bg-rust-500';           title = 'Overdue'; break;
    case 'assumed': cls = 'border-ink-700/20 bg-ink-700/15';      title = 'Before this machine was added — assumed done'; break;
    default:       cls = 'border-ink-700/30 bg-white';
  }
  const interactive = !!onClick && !busy;
  return (
    <button
      type="button"
      title={title}
      onClick={interactive ? onClick : undefined}
      disabled={!interactive}
      className={`${base} ${cls} h-4 w-4 ${interactive ? 'cursor-pointer hover:scale-125' : 'cursor-default'} ${busy ? 'opacity-50' : ''}`}
    />
  );
}

function Legend() {
  return (
    <div className="flex items-center flex-wrap gap-x-3 gap-y-1 text-ink-700/60">
      <Item state="white" label="Upcoming" />
      <Item state="yellow" label="Due" />
      <Item state="green" label="Done" />
      <Item state="red" label="Overdue" />
      <Item state="assumed" label="Assumed done" />
    </div>
  );
}

function PmTaskForm({ task, onSave, onDelete, onCancel }) {
  const [form, setForm] = useState({
    description: task?.description || '',
    interval_hours: task?.interval_hours || '',
    interval_days: task?.interval_days || '',
    last_done_hours: '',
    last_done_at: '',
  });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  function set(k, v) { setForm(f => ({ ...f, [k]: v })); }
  async function submit(e) {
    e.preventDefault();
    setBusy(true); setErr('');
    try { await onSave(form); }
    catch (e) { setErr(e.message); }
    finally { setBusy(false); }
  }
  return (
    <form onSubmit={submit} className="space-y-2">
      <div className="text-sm font-medium text-olive-900">{task ? 'Edit PM task' : 'Add custom PM task'}</div>
      <div>
        <label className="label">Description</label>
        <input className="input" required value={form.description} onChange={e => set('description', e.target.value)} placeholder="Engine oil + filter" />
      </div>
      <div className="grid grid-cols-2 gap-2">
        <div>
          <label className="label">Every (hours)</label>
          <input className="input" type="number" min="0" value={form.interval_hours} onChange={e => set('interval_hours', e.target.value)} placeholder="50" />
        </div>
        <div>
          <label className="label">…or (days)</label>
          <input className="input" type="number" min="0" value={form.interval_days} onChange={e => set('interval_days', e.target.value)} placeholder="180" />
        </div>
      </div>
      <p className="text-xs text-ink-700/60">Set hours, days, or both. Whichever fires first is "due".</p>
      <div className="grid grid-cols-2 gap-2">
        <div>
          <label className="label">Last done at (hours)</label>
          <input className="input" type="number" min="0" step="0.1" inputMode="decimal" value={form.last_done_hours} onChange={e => set('last_done_hours', e.target.value)} placeholder={task?.last_done_hours ?? 'optional'} />
        </div>
        <div>
          <label className="label">Last done on (date)</label>
          <input className="input" type="date" value={form.last_done_at} onChange={e => set('last_done_at', e.target.value)} />
        </div>
      </div>
      <p className="text-xs text-ink-700/60">Optional — record a service done before it was tracked here. Blank keeps the current schedule.</p>
      {err && <div className="text-rust-700 text-xs">{err}</div>}
      <div className="flex gap-2 justify-end items-center">
        {onDelete && (
          <button type="button" onClick={onDelete} disabled={busy} className="mr-auto rounded-md border border-rust-700/40 text-rust-700 text-sm px-3 py-1.5 hover:bg-rust-500/10">Delete task</button>
        )}
        <button type="button" onClick={onCancel} className="btn-ghost text-sm">Cancel</button>
        <button className="btn-primary text-sm" disabled={busy}>{busy ? 'Saving…' : task ? 'Save' : 'Add'}</button>
      </div>
    </form>
  );
}

function Item({ state, label }) {
  const cls = {
    white:  'border-ink-700/30 bg-white',
    yellow: 'border-gold-600 bg-gold-500/30',
    green:  'border-olive-700 bg-olive-700',
    red:    'border-rust-700 bg-rust-500',
    assumed: 'border-ink-700/20 bg-ink-700/15',
  }[state];
  return <span className="inline-flex items-center gap-1 whitespace-nowrap"><span className={`inline-block shrink-0 rounded-full border-2 ${cls} h-3 w-3`} />{label}</span>;
}

// Pop-up sheet: slides up from the bottom on a phone, centered on desktop.
// Used for the task editor so it can't open off-screen below a long grid.
export function Sheet({ children, onClose }) {
  useEffect(() => {
    const onKey = e => { if (e.key === 'Escape') onClose?.(); };
    document.addEventListener('keydown', onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.removeEventListener('keydown', onKey); document.body.style.overflow = prev; };
  }, []);
  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/40" onClick={onClose} role="dialog" aria-modal="true">
      <div
        className="bg-white w-full sm:max-w-md rounded-t-2xl sm:rounded-2xl p-4 shadow-xl max-h-[90vh] overflow-y-auto"
        style={{ paddingBottom: 'max(1rem, env(safe-area-inset-bottom))' }}
        onClick={e => e.stopPropagation()}
      >
        {children}
      </div>
    </div>
  );
}
